import React, { useEffect } from "react";

import { FormProvider } from "react-hook-form";
import { useHistory } from "react-router-dom";

import { theme } from "@/core/theme";
import AddressFinderSearch from "@/components/shared/AddressFinderSearch";
import {
  DASHBOARD_HOME_URL,
  LOGIN_URL,
  REGENERUS_TERMS_AND_CONDITIONS_URL,
  TERMS_OF_SERVICE_URL,
  TERMS_OF_USE_URL
} from "@/core/urls";
import { ACCEPT_PATIENT_INVITE_MUTATION } from "@/graphql/accounts";
import { ACCEPT_PATIENT_INVITE_PAGE_PROPS_QUERY } from "@/graphql/organisations/queries/practitioners";
import useDjangoGraphqlForm from "@/hooks/form/useDjangoGraphqlForm";
import useDocTitle from "@/hooks/use-doc-title";
import useShippingCountries from "@/hooks/useShippingCountries";
import Center from "@/tpo/Center";
import Checkbox from "@/tpo/Checkbox";
import ChevronComponent from "@/tpo/Chevron";
import ControlledFormField from "@/tpo/ControlledFormField";
import FloatingLabelInput from "@/tpo/FloatingLabelInput";
import { Error } from "@/tpo/InputWrapper";
import NativeSelect from "@/tpo/NativeSelect";
import FloatingLabelPasswordInput from "@/tpo/PasswordInput";
import Stack from "@/tpo/Stack";
import B2CHeader from "@/tpo/header/B2CHeader";
import ButtonV2 from "@/v2/Buttons";
import { getShippingAddressFieldNames } from "@/utils/idealPostcodesAddress";

import { useAuthContext } from "./AuthProvider";
import Box from "./Box";
import CannotFindInvite from "./CannotFindInvite";
import CustomerPage, { CustomerAccountPage } from "./CustomerPage";
import DataLoader from "./DataLoader";
import { ExternalTextLink, InternalTextLink } from "./Links";
import ResendEmailVerificationLink from "./ResendEmailVerificationLink";
import Text from "./Text";

function AcceptPatientInviteForm({ token, patientId, props }) {
  useDocTitle("Signup confirmation");

  const firstName = props.patientFirstName || "";
  const lastName = props.patientLastName || "";

  const { user } = useAuthContext();
  const history = useHistory();
  const shippingCountries = useShippingCountries();

  useEffect(() => {
    // If the user had already authenticated, skip this page and head to the dashboard
    if (user) {
      history.push(DASHBOARD_HOME_URL);
    }
    // eslint-disable-next-line
  }, [user]);

  const api = useDjangoGraphqlForm({
    defaultValues: {
      firstName,
      lastName,
      password: "",
      confirmPassword: "",
      shippingName: "",
      shippingLine1: "",
      shippingLine2: "",
      shippingCity: "",
      shippingPostcode: "",
      shippingCountry: "",
      shippingPhoneNumber: "",
      acceptTerms: false
    },
    mutation: ACCEPT_PATIENT_INVITE_MUTATION,
    mutationName: "acceptPatientInviteMutation",
    handleSuccess: () => {
      history.push(LOGIN_URL);
    },
    transformer: data => {
      const { shippingPhoneNumber, ...rest } = data;
      return {
        ...rest,
        shippingPhoneNumber: (shippingPhoneNumber || "").replaceAll(" ", ""),
        patientId,
        token
      };
    }
  });

  const organisationName = props.organisationName;

  return (
    <CustomerPage
      jumbotronProps={{
        title: "Invitation"
      }}
    >
      <Center
        stacked
        gap={[30, 30, 40]}
        pt={theme.spacing.section.pt}
        pb={theme.spacing.section.pb}
        px={20}
      >
        <Box
          fontFamily="gilroyMedium"
          fontSize={[14, 14, 16]}
          maxWidth={760}
          mx="auto"
          width="100%"
        >
          You have been invited to join the Regenerus patient platform by {organisationName}.
          <br />
          <br />
          Please complete the details below to complete your account registration.
        </Box>

        <Stack
          as="form"
          maxWidth={600}
          mx="auto"
          onSubmit={api.methods.handleSubmit(api.onSubmit)}
          gap={20}
          width="100%"
        >
          <FormProvider {...api.methods}>
            <ControlledFormField
              name="firstName"
              label="First name"
              Component={FloatingLabelInput}
            />
            <ControlledFormField name="lastName" label="Last name" Component={FloatingLabelInput} />
            <ControlledFormField
              label="Password"
              name="password"
              Component={FloatingLabelPasswordInput}
            />
            <ControlledFormField
              label="Confirm Password"
              name="confirmPassword"
              Component={FloatingLabelPasswordInput}
            />
            <Box fontFamily="gilroyBold" fontSize={[22, 22, 28]} mt={20}>
              Shipping address
            </Box>
            <AddressFinderSearch
              shippingCountries={shippingCountries}
              fieldNames={getShippingAddressFieldNames({ style: "shippingCamel" })}
              countryValueType="isoCode"
            />
            <ControlledFormField
              name="shippingName"
              label="Name"
              Component={FloatingLabelInput}
            />
            <ControlledFormField
              name="shippingLine1"
              label="Address line 1"
              Component={FloatingLabelInput}
            />
            <ControlledFormField
              name="shippingLine2"
              label="Address line 2"
              Component={FloatingLabelInput}
            />
            <ControlledFormField
              name="shippingCity"
              label="City"
              Component={FloatingLabelInput}
            />
            <ControlledFormField
              name="shippingPostcode"
              label="Postcode"
              Component={FloatingLabelInput}
            />
            <ControlledFormField
              name="shippingPhoneNumber"
              label="Phone number"
              Component={FloatingLabelInput}
            />
            <ControlledFormField
              name="shippingCountry"
              Component={NativeSelect}
              label=""
            >
              <option value="">Select country</option>
              {shippingCountries.map((country, idx) => (
                <option key={`${country.isoCode}-${idx}`} value={country.isoCode}>
                  {country.name}
                </option>
              ))}
            </ControlledFormField>
            <ControlledFormField
              Component={Checkbox}
              name="acceptTerms"
              label={
                <span>
                  I have read and agree to Regenerus{" "}
                  <InternalTextLink href={REGENERUS_TERMS_AND_CONDITIONS_URL}>
                    Terms & Conditions
                  </InternalTextLink>
                </span>
              }
            />
            <ButtonV2
              color="green"
              rightIcon={<ChevronComponent />}
              mx="auto"
              size={["sm", "sm", "md"]}
            >
              submit
            </ButtonV2>
            {!!api.nonFieldError && (
              <Error
                data-testid="nonFieldError"
                error={api.nonFieldError}
                alignSelf="center"
                maxWidth={400}
                textAlign="center"
              />
            )}
          </FormProvider>
        </Stack>
      </Center>
    </CustomerPage>
  );
}

function AcceptPatientInvitePage(props) {
  useDocTitle("Accept Patient Invite");

  return (
    <>
      <B2CHeader />
      <DataLoader
        query={ACCEPT_PATIENT_INVITE_PAGE_PROPS_QUERY}
        variables={{ patientId: props.match.params.patientId, token: props.match.params.token }}
        renderErrorUI={() => (
          <Box px={20} pt={[125, 125, 140]}>
            <Box
              maxWidth={600}
              mx="auto"
              bg="slate"
              color="white"
              p={theme.spacing.medium}
              borderRadius="5px"
            >
              <Text fontFamily="gilroyBold">
                I'm sorry, an error has occured, if you need assistance please contact
                info@regeneruslabs.com
              </Text>
            </Box>
          </Box>
        )}
        render={data => {
          const pageProps = JSON.parse(data.acceptPatientInvitePageProps.pageProps);
          const hasExpired = data.acceptPatientInvitePageProps.hasExpired;
          const justExpired = data.acceptPatientInvitePageProps.justExpired;
          const isOrganisationPatientInvite =
            data.acceptPatientInvitePageProps.organisationPatientInvite;

          if (hasExpired) {
            if (isOrganisationPatientInvite && justExpired) {
              return (
                <CustomerAccountPage
                  jumbotronProps={{
                    title: "Invitation has expired",
                    bg: "purple",
                    color: "white"
                  }}
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                >
                  <Box fontFamily="gilroyMedium" fontSize={[14, 14, 16]} mb={[20, 20, 40]}>
                    This invitation to join the Regenerus patient platform by{" "}
                    {pageProps.practitionerName} from {pageProps.organisationName} has expired. Send
                    a new invite link below to proceed.
                  </Box>
                  <ResendEmailVerificationLink
                    id={props.match.params.patientId} // patientId is in fact the org pat invite id in this case
                    variableName="organisationPatientInvite"
                    size={"sm"}
                    buttonText="Resend invite"
                    color="green"
                  />
                </CustomerAccountPage>
              );
            }

            return (
              <CannotFindInvite
                title="Invitation not found"
                headerBg="purple"
                color="white"
                message="I'm sorry, we can't seem to find that patient invite. Please contact info@regeneruslabs.com."
              />
            );
          }
          return (
            <AcceptPatientInviteForm
              token={props.match.params.token}
              patientId={props.match.params.patientId}
              props={pageProps}
            />
          );
        }}
      />
    </>
  );
}

export default AcceptPatientInvitePage;
